import React, { useState, useEffect, createContext, useContext } from 'react';
import { BrowserRouter, Routes, Route, Navigate, Link, useNavigate } from 'react-router-dom';
import Dashboard from './pages/Dashboard';
import Users from './pages/Users';
import Helpers from './pages/Helpers';
import Requests from './pages/Requests';
import Ports from './pages/Ports';
import Login from './pages/Login';

const API_BASE = '/api';

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

export const api = async (endpoint, options = {}) => {
  const token = localStorage.getItem('adminToken');
  const res = await fetch(`${API_BASE}${endpoint}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
      ...options.headers,
    },
  });
  const data = await res.json();
  if (!res.ok) {
    const errorMsg = data.error?.message || data.error || data.message || 'API Error';
    throw new Error(typeof errorMsg === 'string' ? errorMsg : JSON.stringify(errorMsg));
  }
  return data;
};

function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    const userData = localStorage.getItem('adminUser');
    if (token && userData) {
      try {
        setUser(JSON.parse(userData));
      } catch (e) {
        localStorage.removeItem('adminToken');
        localStorage.removeItem('adminUser');
      }
    }
    setLoading(false);
  }, []);

  const login = async (email, password) => {
    const res = await api('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
    if (!res.success) {
      throw new Error(res.error?.message || res.error || 'Login failed');
    }
    if (res.data?.user?.user_type !== 'admin') {
      throw new Error('Admin access only');
    }
    localStorage.setItem('adminToken', res.data.access_token);
    localStorage.setItem('adminUser', JSON.stringify(res.data.user));
    setUser(res.data.user);
  };

  const logout = () => {
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminUser');
    setUser(null);
  };

  if (loading) return <div style={{ padding: 40, textAlign: 'center' }}>Loading...</div>;

  return (
    <AuthContext.Provider value={{ user, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div style={{ display: 'flex', minHeight: '100vh' }}>
      <nav style={{
        width: 240,
        background: '#1a1a2e',
        color: 'white',
        padding: '20px 0',
        position: 'relative',
      }}>
        <div style={{ padding: '0 20px 30px', borderBottom: '1px solid #333' }}>
          <h2 style={{ fontSize: 20, fontWeight: 700 }}>AnchorPals</h2>
          <p style={{ fontSize: 12, color: '#888', marginTop: 4 }}>Admin Panel</p>
        </div>
        <div style={{ padding: '20px 0' }}>
          {[
            { path: '/', label: 'Dashboard', icon: '📊' },
            { path: '/users', label: 'Users', icon: '👥' },
            { path: '/helpers', label: 'Helpers', icon: '🤝' },
            { path: '/requests', label: 'Requests', icon: '📋' },
            { path: '/ports', label: 'Ports', icon: '⚓' },
          ].map(item => (
            <Link key={item.path} to={item.path} style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              padding: '12px 20px',
              color: '#ccc',
              transition: 'all 0.2s',
            }}>
              <span>{item.icon}</span>
              <span>{item.label}</span>
            </Link>
          ))}
        </div>
        <div style={{ position: 'absolute', bottom: 20, left: 0, width: 240, padding: '0 20px' }}>
          <div style={{ fontSize: 13, color: '#888', marginBottom: 10 }}>
            {user?.email}
          </div>
          <button onClick={handleLogout} style={{
            width: '100%',
            padding: '10px',
            background: '#333',
            border: 'none',
            borderRadius: 8,
            color: '#ccc',
            cursor: 'pointer',
          }}>
            Logout
          </button>
        </div>
      </nav>
      <main style={{ flex: 1, padding: 30, background: '#f5f7fa', overflowY: 'auto' }}>
        {children}
      </main>
    </div>
  );
}

function ProtectedRoute({ children }) {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  return <Layout>{children}</Layout>;
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
          <Route path="/users" element={<ProtectedRoute><Users /></ProtectedRoute>} />
          <Route path="/helpers" element={<ProtectedRoute><Helpers /></ProtectedRoute>} />
          <Route path="/requests" element={<ProtectedRoute><Requests /></ProtectedRoute>} />
          <Route path="/ports" element={<ProtectedRoute><Ports /></ProtectedRoute>} />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
